<template>
  <v-layout align-center row wrap fill-height class="date-range-picker text-xs-center">
    <v-flex xs12 md6>
      <v-subheader v-if="labels.start" style="padding: 0;">
        {{ labels.start }}:&nbsp;
        <span v-if="formattedStartDate">{{ formattedStartDate }}</span>
        <span v-else-if="placeholders.start">{{ placeholders.start }}</span>
        <div style="margin-left:1rem; cursor: pointer;"
          v-if="startDate && $attrs.clearable && $attrs.clearable.start"
        >
          <v-tooltip bottom>
            <v-icon slot="activator" @click="startDate = null">mdi-close</v-icon>
            <span>Click to clear</span>
          </v-tooltip>
        </div>
      </v-subheader>
      <v-input :value="startDate" :hint="hints.start" :persistent-hint="!startDate" :rules="$attrs.rules.start">
        <v-date-picker class="date-range-picker--from"
          v-model="startDate"
          ref="start"
          v-bind="$attrs"
          v-on="listeners"
          :max="endDate"
          :events="highlightRange ? dateRange.dates : events"
          :event-color="highlightRange ? dateRange.colors : eventColor"
          full-width
        />
      </v-input>
    </v-flex>
    <v-flex xs12 md6>
      <v-subheader v-if="labels.end" style="padding: 0;">
        {{ labels.end }}:&nbsp;
        <span v-if="formattedEndDate">{{ formattedEndDate }}</span>
        <span v-else-if="placeholders.end">{{ placeholders.end }}</span>
        <div style="margin-left:1rem; cursor: pointer;"
          v-if="endDate && $attrs.clearable && $attrs.clearable.end"
        >
          <v-tooltip bottom>
            <v-icon slot="activator" @click="endDate = null">mdi-close</v-icon>
            <span>Click to clear</span>
          </v-tooltip>
        </div>
      </v-subheader>
      <v-input required :value="endDate" :hint="hints.end" :persistent-hint="!endDate" :rules="$attrs.rules.end">
        <v-date-picker class="date-range-picker--to"
          v-model="endDate"
          ref="end"
          v-bind="$attrs"
          v-on="listeners"
          :min="startDate"
          :events="highlightRange ? dateRange.dates : events"
          :event-color="highlightRange ? dateRange.colors : eventColor"
          full-width
        />
      </v-input>
    </v-flex>
  </v-layout>
</template>

<script>
export default {
  // So $attrs can be passed to date pickers
  inheritAttrs: false,

  created() {
    this.yearsFirst && this.$nextTick(() => {
      this.$refs.start.activePicker = 'YEAR'
      this.$refs.end.activePicker = 'YEAR'
    })
  },

  props: {
    format: { type: String, default: 'yyyy-MM-dd' },
    highlightColors: { type: [String, Function, Object], default: 'warning' },
    highlightRange: { type: Boolean, default: true, },
    labels: { type: Object, default: () => ({ start: 'Start Date', end: 'End Date' }) },
    placeholders: { type: Object, default: () => ({ start: 'Select Date', end: 'Select Date' }) },
    hints: { type: Object, default: () => ({ start: '', end: '' }) },
    yearsFirst: { type: Boolean, default: false }
  },

  data() {
    return {
      startDate: null,
      endDate: null,
      dateRange: {
        dates: [],
        colors: {},
      }
    }
  },

  computed: {
    listeners() {
      return {
        ...this.$listeners,
        input: event => this.$emit('input', [this.startDate, this.endDate])
      }
    },

    formattedStartDate() {
      if (this.startDate) return this.$date(new Date(this.startDate), this.format)
      return null
    },

    formattedEndDate() {
      if (this.endDate) return this.$date(new Date(this.endDate), this.format)
      return null
    },

    highlightColorClasses() {
      if (this.highlightColors) {
        return this.highlightColors
      }
      return this.$attrs.dark ? 'blue-grey darken-1' : 'blue lighten-5'
    },

    today() {
      return this.$date(new Date(), 'yyyy-MM-dd')
    },

    maxDate() {
      return this.max || this.today
    }
  },

   watch: {
    startDate() {
      this.onDateRangeChange()
    },

    endDate() {
      this.onDateRangeChange()
    },
  },

  methods: {
    onDateRangeChange() {
      if (this.highlightRange) this.setInRangeData()
    },

    setInRangeData() {
      const inRangeData = { dates: [], colors: {} }

      if (this.highlightRange && this.startDate && this.endDate) {
        const startDate = new Date(this.startDate)
        const endDate = new Date(this.endDate)
        const diffDays = (endDate - startDate) / (1000 * 3600 * 24)

        for(let i of [...Array(diffDays + 1).keys()]) {
          const date = this.addDays(startDate.toDateString(), i)
          inRangeData.dates.push(date)
          inRangeData.colors[date] = `date-range-picker__date-in-range ${this.highlightColorClasses}`
          if (i === 0) inRangeData.colors[date] += ' date-range-picker__range-start'
          if (i === diffDays) inRangeData.colors[date] += ' date-range-picker__range-end'
        }
      }

      this.dateRange = inRangeData
    },

    addDays(date, days) {
      const result = new Date(date)
      result.setDate(result.getDate() + days)
      return this.$date(result, 'yyyy-MM-dd')
    }
  }
}
</script>

<style lang="scss">
.date-range-picker {
  & div.v-picker.v-card {
    box-shadow: 0 0 0 0 transparent;
  }

  & .v-date-picker-table {
    & table {
      border-collapse: collapse;
    }

    & .v-btn {
      /* because .v-date-picker-table__event is created after the .v-btn */
      z-index: 1;
    }
  }

  & .v-date-picker-table__event.date-range-picker__date-in-range {
    z-index: 0;
    /* override existing settings */
    width: 100%;
    height: 100%;
    left: 0;
    top: 0;
    bottom: 0;
    border-radius: 0;

    &.date-range-picker__range-start {
      left: 50%;
    }

    &.date-range-picker__range-end {
      width: 60%;
    }
  }

  &
    button:not(.v-btn--active)
    + .v-date-picker-table__event.date-range-picker__date-in-range.date-range-picker__range-start {
    left: 0%;
    border-top-left-radius: 17.5px;
    border-bottom-left-radius: 17.5px;
  }

  &
    .date-range-picker--from
    .v-date-picker-table__event.date-range-picker__date-in-range.date-range-picker__range-end {
    width: 100%;
    border-top-right-radius: 17.5px;
    border-bottom-right-radius: 17.5px;
  }
}
</style>
